import { AccountingExpense } from 'types';
import { formatPrice } from 'utils/helpers';
import {
  CAR_RENTAL_CATEGORY_ID,
  RENTAL_COMPANY_OPTIONS,
  getCarRentalAmountState,
  CAR_RENTAL_AMOUNT_LABELS,
} from 'components/accounting-expenses-form/config';

export interface CarRentalField {
  title: string;
  value?: string;
}

// The View Expense "Car rental" card (Owner, 15 Oct 2026: "car rental
// expense should show the renter name, reservation # etc").
//
// Field names are the ones Main returns on GET /accounting/expense/:id
// (`Expense.RentalDetail`, as 'rentalDetail', in the service's common
// include). The card shows for any expense that has saved rental detail, and
// for a Car Rental expense saved before the detail existed (its fields then
// read as the empty placeholder). Agreement # is no longer entered (Owner,
// 13 Oct 2026), so it shows only when an older rental or a statement stored
// one. The estimate is the expense amount until a statement sets the final
// amount (Owner, 18 Oct 2026: "we don't need both"), so Estimated Amount
// shows only once the two differ. Returns null for every other expense:
// nothing extra is shown.
export const getCarRentalFields = (
  data?: AccountingExpense
): CarRentalField[] | null => {
  const detail = data?.rentalDetail;
  const isCarRental = data?.categories?.some(
    (category) => Number(category.id) === CAR_RENTAL_CATEGORY_ID
  );

  if (!detail && !isCarRental) {
    return null;
  }

  const company = RENTAL_COMPANY_OPTIONS.find(
    (option) => option.value === detail?.rentalCompany
  );
  const estimate = detail?.estimatedAmountCents;
  const isFinal = getCarRentalAmountState(data) === 'final';
  const agreement = detail?.agreementNumber?.trim();

  return [
    { title: 'Renter Name', value: detail?.renterName?.trim() || undefined },
    {
      title: 'Rental Company',
      value: company?.label ?? (detail?.rentalCompany || undefined),
    },
    {
      title: 'Reservation Number',
      value: detail?.reservationNumber?.trim() || undefined,
    },
    ...(isFinal && estimate != null
      ? [
          {
            title: 'Estimated Amount',
            value: formatPrice(Number(estimate) / 100),
          },
        ]
      : []),
    ...(agreement ? [{ value: agreement, title: 'Agreement Number' }] : []),
  ];
};

// The summary card's Amount title: on a Car Rental expense it says which
// amount it is (Estimated / Final), like the form. A split keeps "Amount":
// that figure is the whole payment.
export const getExpenseAmountTitle = (data?: AccountingExpense) =>
  data?.categories?.length === 1 &&
  Number(data.categories[0].id) === CAR_RENTAL_CATEGORY_ID
    ? CAR_RENTAL_AMOUNT_LABELS[getCarRentalAmountState(data)]
    : 'Amount';
